
// Create TypeScript interfaces for our records
export interface BackupRecord {
  id: string;
  websiteId: string;
  createdAt: string;
  size: string;
  status: 'completed' | 'failed' | 'in-progress';
  downloadUrl?: string;
  progress?: number; // Progress property for tracking backup progress
  backupType?: 'full' | 'incremental'; // Added missing backupType property
}

// Interface for website records
interface Website {
  id: string;
  title: string;
  url: string;
  platform: 'wordpress' | 'shopify' | 'woocommerce';
  lastBackup: string;
  nextBackup: string;
  storageUsed: number;
  status: 'active' | 'paused';
}

// Interface for WordPress credentials
interface WordPressCredentials {
  siteUrl: string;
  username: string;
  password: string;
}

/**
 * Initialize the database
 */
export const initDatabase = async (): Promise<boolean> => {
  try {
    // For a browser-based app, we're just ensuring localStorage exists
    if (typeof localStorage === 'undefined') {
      console.error('localStorage is not available');
      return false;
    }
    
    // Set up initial data structures if they don't exist
    if (!localStorage.getItem('wordpress_backups')) {
      localStorage.setItem('wordpress_backups', JSON.stringify([]));
    }
    
    if (!localStorage.getItem('websites')) {
      localStorage.setItem('websites', JSON.stringify([]));
    }
    
    if (!localStorage.getItem('credentials')) {
      localStorage.setItem('credentials', JSON.stringify({}));
    }
    
    return true;
  } catch (error) {
    console.error('Error initializing database:', error);
    return false;
  }
};

/**
 * Gets all backups from localStorage
 */
export const getBackups = (): BackupRecord[] => {
  try {
    const backupsData = localStorage.getItem('wordpress_backups');
    if (backupsData) {
      return JSON.parse(backupsData);
    }
    return [];
  } catch (error) {
    console.error('Error getting backups from localStorage:', error);
    return [];
  }
};

/**
 * Saves backups to localStorage
 */
export const saveBackups = (backups: BackupRecord[]): boolean => {
  try {
    localStorage.setItem('wordpress_backups', JSON.stringify(backups));
    return true;
  } catch (error) {
    console.error('Error saving backups to localStorage:', error);
    return false;
  }
};

/**
 * Gets all websites from localStorage
 */
export const getAllWebsites = async (): Promise<Website[]> => {
  try {
    const websitesData = localStorage.getItem('websites');
    if (websitesData) {
      return JSON.parse(websitesData);
    }
    return [];
  } catch (error) {
    console.error('Error getting websites from localStorage:', error);
    return [];
  }
};

/**
 * Gets a website by ID from localStorage
 */
export const getWebsiteById = async (websiteId: string): Promise<Website | null> => {
  try {
    const websites = await getAllWebsites();
    return websites.find(website => website.id === websiteId) || null;
  } catch (error) {
    console.error(`Error getting website ${websiteId} from localStorage:`, error);
    return null;
  }
};

/**
 * Saves a website to localStorage
 */
export const saveWebsite = async (website: Website): Promise<boolean> => {
  try {
    const websites = await getAllWebsites();
    const existingIndex = websites.findIndex(w => w.id === website.id);
    
    if (existingIndex !== -1) {
      websites[existingIndex] = website;
    } else {
      websites.push(website);
    }
    
    localStorage.setItem('websites', JSON.stringify(websites));
    return true;
  } catch (error) {
    console.error(`Error saving website ${website.id} to localStorage:`, error);
    return false;
  }
};

/**
 * Updates the last backup date for a website
 */
export const updateLastBackup = async (websiteId: string, lastBackup: string): Promise<boolean> => {
  try {
    const website = await getWebsiteById(websiteId);
    if (!website) {
      return false;
    }
    
    website.lastBackup = lastBackup;
    return await saveWebsite(website);
  } catch (error) {
    console.error(`Error updating last backup for website ${websiteId}:`, error);
    return false;
  }
};

/**
 * Gets WordPress credentials for a website
 */
export const getCredentials = async (websiteId: string): Promise<WordPressCredentials | null> => {
  try {
    const credentialsData = localStorage.getItem('credentials');
    if (!credentialsData) {
      return null;
    }
    
    const allCredentials = JSON.parse(credentialsData);
    return allCredentials[websiteId] || null;
  } catch (error) {
    console.error(`Error getting credentials for website ${websiteId}:`, error);
    return null;
  }
};

/**
 * Saves WordPress credentials for a website
 */
export const saveCredentials = async (
  websiteId: string, 
  credentials: WordPressCredentials
): Promise<boolean> => {
  try {
    let allCredentials = {};
    const credentialsData = localStorage.getItem('credentials');
    
    if (credentialsData) {
      allCredentials = JSON.parse(credentialsData);
    }
    
    allCredentials[websiteId] = credentials;
    localStorage.setItem('credentials', JSON.stringify(allCredentials));
    return true;
  } catch (error) {
    console.error(`Error saving credentials for website ${websiteId}:`, error);
    return false;
  }
};

/**
 * Saves a backup record
 */
export const saveBackupRecord = async (
  backupId: string,
  websiteId: string,
  status: 'completed' | 'failed' | 'in-progress',
  size: string,
  downloadUrl?: string,
  progress?: number
): Promise<boolean> => {
  try {
    const backups = getBackups();
    
    const newBackup: BackupRecord = {
      id: backupId,
      websiteId,
      createdAt: new Date().toISOString(),
      size,
      status,
      downloadUrl,
      progress,
      backupType: 'full' // Default backup type
    };
    
    backups.push(newBackup);
    return saveBackups(backups);
  } catch (error) {
    console.error(`Error saving backup record ${backupId}:`, error);
    return false;
  }
};

/**
 * Updates an existing backup record
 */
export const updateBackupRecord = async (
  backupId: string,
  updates: Partial<BackupRecord>
): Promise<boolean> => {
  try {
    const backups = getBackups();
    const backupIndex = backups.findIndex(b => b.id === backupId);
    
    if (backupIndex === -1) {
      console.error(`Backup ${backupId} not found for update`);
      return false;
    }
    
    backups[backupIndex] = {
      ...backups[backupIndex],
      ...updates
    };
    
    return saveBackups(backups);
  } catch (error) {
    console.error(`Error updating backup record ${backupId}:`, error);
    return false;
  }
};

/**
 * Seeds demo data for testing
 */
export const seedDemoData = async (): Promise<boolean> => {
  try {
    await initDatabase();
    
    // Seed sample websites
    const sampleWebsites: Website[] = [
      {
        id: "1",
        title: "My WordPress Blog",
        url: "https://myblog.example.com",
        platform: "wordpress",
        lastBackup: "3 days ago",
        nextBackup: "Tomorrow",
        storageUsed: 450,
        status: "active"
      },
      {
        id: "2",
        title: "Online Shop",
        url: "https://shop.example.com",
        platform: "woocommerce",
        lastBackup: "1 week ago",
        nextBackup: "3 days",
        storageUsed: 1200,
        status: "active"
      },
      {
        id: "3",
        title: "Product Store",
        url: "https://store.example.com",
        platform: "shopify",
        lastBackup: "5 days ago",
        nextBackup: "2 days",
        storageUsed: 890,
        status: "paused"
      },
      {
        id: "4",
        title: "Drive Expert",
        url: "https://www.drive-expert.com",
        platform: "wordpress",
        lastBackup: "Just now",
        nextBackup: "Tomorrow",
        storageUsed: 720,
        status: "active"
      }
    ];
    
    // Save each website
    for (const website of sampleWebsites) {
      await saveWebsite(website);
    }
    
    // Seed sample credentials
    const sampleCredentials = {
      "1": {
        siteUrl: "https://myblog.example.com",
        username: "admin",
        password: "password123"
      },
      "2": {
        siteUrl: "https://shop.example.com",
        username: "shopadmin",
        password: "shop123"
      },
      "3": {
        siteUrl: "https://store.example.com",
        username: "storeuser",
        password: "store456"
      },
      "4": {
        siteUrl: "https://www.drive-expert.com",
        username: "driveexp",
        password: "##Test$$Blog##"
      }
    };
    
    // Save credentials
    localStorage.setItem('credentials', JSON.stringify(sampleCredentials));
    
    return true;
  } catch (error) {
    console.error('Error seeding demo data:', error);
    return false;
  }
};
